import { useDispatch } from 'react-redux';
import { useUploadEvaluationEvidenceMutation } from '../services/SkillsManagement';
import { ADD_PRACTICAL_EVIDENCE } from '../Reducers/Actions';
import { toast } from 'react-toastify';

/**
 * useUploadEvaluationEvidences
 * Encapsulates evidence upload flow for an evaluation.
 *
 * API returns:
 * { success: true, message, data: { evidence_id, evaluation_id, evidence_type, evidence_url, ... } }
 */
export default function useUploadEvaluationEvidences() {
  const dispatch = useDispatch();
  const [uploadEvaluationEvidence] = useUploadEvaluationEvidenceMutation();

  /**
   * Upload files for a single skill within an evaluation
   * @param {Object} params
   * @param {string|number} params.evaluationId
   * @param {number} params.skillId
   * @param {Array<File|{file: File}>} params.files
   * @param {string|null} params.level
   * @param {number|null} params.score
   * @param {string} params.comment
   */
  const uploadForSkill = async ({ evaluationId, skillId, files = [], level = null, score = null, comment = '' }) => {
    if (!evaluationId || !Array.isArray(files) || files.length === 0) return [];
    const uploaded = [];

    for (const f of files) {
      try {
        const blob = f instanceof File ? f : (f?.file instanceof File ? f.file : null);
        if (!blob) continue;
        const fd = new FormData();
        const mime = blob.type || '';
        const type = mime.startsWith('image/') ? 'image' : mime.startsWith('video/') ? 'video' : 'document';
        fd.append('evidence_type', type);
        fd.append('file', blob, blob.name || 'upload');
        const metadata = { skill_id: skillId, level, score, comment };
        try { fd.append('metadata', JSON.stringify(metadata)); } catch (_) {}

        const res = await uploadEvaluationEvidence({ evaluationId: String(evaluationId), body: fd }).unwrap();
        // Handle antivirus scan results from backend
        const scanResults = Array.isArray(res?.scanResults) ? res.scanResults : [];
        for (const sr of scanResults) {
          if (sr?.isInfected) {
            const v = Array.isArray(sr?.viruses) ? sr.viruses.join(', ') : 'unknown virus';
            toast.error(`Security scan failed for ${sr?.file || blob.name || 'file'}: ${v}`);
          }
        }
        const evidenceObj = res?.data || res?.evidence || res;
        if (evidenceObj) {
          // Enrich with client-side fields for immediate UI use
          const url = evidenceObj.evidence_url || evidenceObj.url;
          const derivedName = blob.name || (url ? String(url).split('/').pop() : undefined);
          const normalized = {
            ...evidenceObj,
            evaluation_id: evidenceObj.evaluation_id || evaluationId,
            evidence_type: evidenceObj.evidence_type || type,
            evidenceType: evidenceObj.evidenceType || evidenceObj.evidence_type || type,
            file_name: evidenceObj.file_name || derivedName,
            url: url || evidenceObj.url,
            evidence_url: url || evidenceObj.evidence_url,
            status: evidenceObj.status || 'pending',
            metadata: evidenceObj.metadata || metadata,
            // attach scan results (if any) for UI consumers
            _scanResults: scanResults,
          };
          uploaded.push(normalized);
          dispatch({ type: ADD_PRACTICAL_EVIDENCE, payload: normalized });
        }
      } catch (err) {
        // continue other files
        // eslint-disable-next-line no-console
        console.error('Evidence upload failed', err);
      }
    }
    return uploaded;
  };

  return { uploadForSkill };
}
